package uz.ferro.erp.feature.task.ui

import emotion.react.css
import js.objects.jso
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import mui.icons.material.Description
import mui.icons.material.DownloadForOffline
import mui.icons.material.PostAdd
import mui.material.Box
import mui.material.Button
import mui.material.CircularProgress
import mui.material.Dialog
import mui.material.DialogActions
import mui.material.DialogContent
import mui.material.DialogTitle
import mui.material.FormControlVariant
import mui.material.Paper
import mui.material.Size
import mui.material.TextField
import mui.material.Typography
import mui.system.sx
import react.FC
import react.ReactNode
import react.dom.events.ChangeEvent
import react.dom.html.ReactHTML.input
import react.dom.onChange
import react.router.useOutletContext
import react.useContext
import react.useEffect
import react.useEffectOnce
import react.useRef
import react.useState
import uz.ferro.erp.Colors
import uz.ferro.erp.NavigationContext
import uz.ferro.erp.feature.alert.AlertManagerContext
import uz.ferro.erp.feature.home.ui.DashboardUI
import uz.ferro.erp.feature.menu.model.MenuItem
import uz.ferro.erp.feature.task.data.ExportTasksToExcelCase
import uz.ferro.erp.feature.task.data.TaskRepository
import uz.ferro.erp.feature.task.model.TaskStatus
import uz.ferro.erp.feature.task.model.dto.TaskDto
import uz.ferro.erp.feature.task.model.dto.TaskFilterDto
import uz.ferro.erp.ui.AppTextMedium
import uz.ferro.erp.ui.HorizontalLayout
import uz.ferro.erp.ui.VerticalLayout
import uz.ferro.erp.ui.table.CellItem
import uz.ferro.erp.ui.table.HeaderItem
import uz.ferro.erp.ui.table.RowItem
import uz.ferro.erp.ui.table.TableView
import uz.ferro.erp.util.currentDateYMD
import uz.ferro.erp.util.displayText
import uz.ferro.erp.util.formatDMY
import uz.ferro.erp.util.generateUUID
import uz.ferro.erp.util.getCurrentDateTimeSuffix
import web.cssom.AlignItems
import web.cssom.Auto
import web.cssom.JustifyContent
import web.cssom.Position
import web.cssom.Visibility
import web.cssom.number
import web.cssom.pct
import web.cssom.px
import web.file.File
import web.html.HTMLInputElement
import web.html.InputType
import web.url.URL

private var filteredDataForExport = emptyList<TaskDto>()

private var filter = TaskFilterDto(
    assignedTo = null,
    customerId = null,
    dateFrom = currentDateYMD(),
    dateTo = currentDateYMD()
)

val TaskListPage = FC {
    val navigator = useContext(NavigationContext)
    val alertManager = useContext(AlertManagerContext)
    val dashboardUI = useOutletContext<DashboardUI>()

    var searchQuery by useState("")
    var tasks by useState<List<TaskDto>>(emptyList())

    var fileAttachInputRef = useRef<HTMLInputElement>(null)
    var importDialogOpen by useState(false)
    var importInProgress by useState(false)
    var importErrors: List<String> by useState(emptyList())
    var importTasks: List<TaskDto> by useState(emptyList())
    var saveImportInProgress by useState(false)
    var filteredTasks by useState<List<TaskDto>>(emptyList())

    var filterState by useState(filter)

    fun runFilter(data: List<TaskDto> = tasks) {
        val filteredData = if (searchQuery.isBlank()) {
            data
        } else {
            data.filter { task ->
                task.customer?.id?.contains(searchQuery, ignoreCase = true) == true ||
                        task.customer?.name?.contains(searchQuery, ignoreCase = true) == true ||
                        task.description?.contains(searchQuery, ignoreCase = true) == true ||
                        task.assignedTo?.name?.contains(searchQuery, ignoreCase = true) == true
            }
        }

        filteredTasks = filteredData
        filteredDataForExport = filteredData.toMutableList().toList()
    }

    fun loadData() {
        MainScope().launch {
            try {
                val result = TaskRepository.getTasks(filter)
                tasks = result
                runFilter(result)
            } catch (e: Exception) {
                // no-op
            }
        }
    }

    fun onImportAttach(file: File) {
        MainScope().launch {
            try {
                importDialogOpen = true
                importInProgress = true
                val result = TaskRepository.importFromExcel(file)
                importTasks = result.tasks.map {
                    it.copy(
                        uuid = generateUUID(),
                        status = TaskStatus.ASSIGNED
                    )
                }
                importErrors = result.errors
                fileAttachInputRef.current?.value = ""
            } catch (e: Exception) {
                alertManager.handleError(e)
            }
            importInProgress = false
        }
    }

    fun exportToExcel() {
        MainScope().launch {
            val blob = ExportTasksToExcelCase.exportToExcel(filteredDataForExport)
            val url = URL.createObjectURL(blob)

            val outputName = "tasks_${getCurrentDateTimeSuffix()}.xlsx"


            var a = document.createElement("a").asDynamic()
            document.body?.appendChild(a)
            a.style.display = "none"
            a.href = url
            a.download = outputName
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    fun closeImportDialog() {
        if (saveImportInProgress || importInProgress) {
            return
        }
        importDialogOpen = false
        importInProgress = false
        importTasks = emptyList()
        importErrors = emptyList()
        fileAttachInputRef.current?.value = ""
    }

    fun saveImportedTasks() {
        MainScope().launch {
            try {
                saveImportInProgress = true
                TaskRepository.addTasksBatch(importTasks)
                closeImportDialog()
                loadData()
            } catch (e: Exception) {
                alertManager.handleError(e)
            }
            saveImportInProgress = false
        }
    }

    useEffectOnce {
        loadData()
        dashboardUI.setTitle("Vazifalar")
        dashboardUI.setMenuItems(
            page = "task",
            items = listOf(
                MenuItem(
                    icon = Description,
                    title = "Shablon",
                    action = {
                        navigator.openUrl("/data/tasks_template.xlsx")
                    }
                ),
                MenuItem(
                    icon = PostAdd,
                    title = "Import",
                    action = {
                        fileAttachInputRef.current?.click()
                    }
                ),
                MenuItem(
                    icon = DownloadForOffline,
                    title = "Export",
                    action = {
                        exportToExcel()
                    }
                )
            )
        )
    }

    useEffect(searchQuery, tasks) {
        runFilter()
    }

    Box {
        sx {
            visibility = Visibility.hidden
            position = Position.absolute
        }
        input {
            this.ref = fileAttachInputRef
            multiple = false
            css {
                margin = 5.px
                position = Position.absolute
                visibility = Visibility.hidden
            }
            type = InputType.file
            accept =
                ".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange = { event ->
                val file = event.target.files?.item(0)
                if (file != null) {
                    onImportAttach(file)
                }
            }
        }
    }

    VerticalLayout {
        space = 16.px
        sx {
            padding = 16.px
            alignItems = AlignItems.stretch
            flexGrow = number(1.0)
        }

        HorizontalLayout {
            space = 16.px


            TextField {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                    width = 250.px
                }
                placeholder = "Qidiruv"
                size = Size.small
                variant = FormControlVariant.outlined
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    searchQuery = event.target.value
                }
            }

            TextField {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                    width = 200.px
                }
                label = ReactNode("Sana (dan)")
                InputLabelProps = jso {
                    shrink = true
                }
                value = filterState.dateFrom.orEmpty()
                size = Size.small
                variant = FormControlVariant.outlined
                type = InputType.date
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    val value = event.target.value
                    filter = filter.copy(
                        dateFrom = value
                    )
                    filterState = filter
                    loadData()
                }
            }

            TextField {
                sx {
                    paddingTop = 4.px
                    paddingBottom = 4.px
                    width = 200.px
                }
                InputLabelProps = jso {
                    shrink = true
                }
                label = ReactNode("Sana (gacha)")
                value = filterState.dateTo.orEmpty()
                size = Size.small
                variant = FormControlVariant.outlined
                type = InputType.date
                onChange = { event ->
                    event as ChangeEvent<HTMLInputElement>
                    val value = event.target.value
                    filter = filter.copy(
                        dateTo = value
                    )
                    filterState = filter
                    loadData()
                }
            }
        }

        Paper {
            sx {
                position = Position.relative
                width = 100.pct
                flexGrow = number(1.0)
                overflowY = Auto.auto
            }

            TableView {
                headers = listOf(
                    HeaderItem("ID"),
                    HeaderItem("Turi"),
                    HeaderItem("Sana"),
                    HeaderItem("Mijoz"),
                    HeaderItem("Bajaruvchi"),
                    HeaderItem("Tavsif"),
                    HeaderItem("Holati"),
                    HeaderItem("Izoh"),
                )
                items = filteredTasks.mapIndexed { index, task ->
                    RowItem(
                        key = index.toString(),
                        cells = listOf(
                            CellItem(
                                title = task.id?.toString()
                            ),
                            CellItem(
                                title = task.typeName
                            ),
                            CellItem(
                                title = task.date.formatDMY()
                            ),
                            CellItem(
                                title = task.customer?.id
                            ),
                            CellItem(
                                title = task.assignedTo?.name.toString()
                            ),
                            CellItem(
                                title = task.description
                            ),
                            CellItem(
                                title = task.resultTypeName
                            ),
                            CellItem(
                                title = task.resultComment
                            )
                        )
                    )
                }
            }
        }
    }

    Dialog {
        open = importDialogOpen
        fullWidth = true
        maxWidth = "lg"
        onClose = { _, _ ->
            closeImportDialog()
        }

        DialogTitle {
            +"Import"
        }

        DialogContent {
            if (importInProgress) {
                VerticalLayout {
                    sx {
                        width = 100.pct
                        height = 200.px
                        alignItems = AlignItems.center
                        justifyContent = JustifyContent.center
                    }
                    CircularProgress()
                }
            }

            if (importErrors.isNotEmpty()) {
                AppTextMedium {
                    size = 20
                    color = Colors.errorText
                    text = "Fayldagi xatolar:"
                }
                mui.material.List {
                    importErrors.forEach {
                        Typography {
                            sx {
                                color = Colors.errorText
                            }
                            +it
                        }
                    }
                }
            } else {
                if (importTasks.isNotEmpty()) {
                    Box {
                        TableView {
                            positionAutoHeight = true
                            headers = listOf(
                                HeaderItem("Mijoz"),
                                HeaderItem("Turi"),
                                HeaderItem("Sana"),
                                HeaderItem("Bajaruvchi"),
                                HeaderItem("Tavsif"),
                                HeaderItem("Hujjat turi"),
                                HeaderItem("Hujjat raqami")
                            )
                            items = importTasks.mapIndexed { index, task ->
                                RowItem(
                                    key = index.toString(),
                                    cells = listOf(
                                        CellItem(
                                            title = task.customer?.id
                                        ),
                                        CellItem(
                                            title = task.typeName
                                        ),
                                        CellItem(
                                            title = task.date.formatDMY()
                                        ),
                                        CellItem(
                                            title = task.assignedTo?.name.toString()
                                        ),
                                        CellItem(
                                            title = task.description
                                        ),
                                        CellItem(
                                            title = task.targetType.displayText()
                                        ),
                                        CellItem(
                                            title = task.targetDocNum?.toString()
                                        ),
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }

        DialogActions {
            if (saveImportInProgress) {
                Box {
                    sx {
                        paddingRight = 16.px
                    }

                    CircularProgress {
                        size = 32
                    }
                }
            }

            Button {
                disabled = importInProgress || saveImportInProgress
                onClick = {
                    closeImportDialog()
                }
                +"Bekor"
            }

            Button {
                disabled = importInProgress || importTasks.isEmpty() || saveImportInProgress
                onClick = {
                    saveImportedTasks()
                }
                +"Saqlash"
            }
        }
    }
}